import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, ClickAwayListener, Grow, MenuItem, MenuList,
  Paper, Popper, Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import SectorFilter from './SectorFilter';
import MarketCapFilter, { detectCapFromMetrics, capToTextClauses } from './MarketCapFilter';
import MetricFilter from './MetricFilter';
import {
  parseFiltersFromText,
  updateTextWithSectors,
  updateTextWithMetric,
  removeFilterLine,
} from './filterSync';

const POPULAR_METRICS = [
  'PE', 'PS', 'ROE', 'Revenues TTM', 'Dividend Yield', 'Debt Equity',
  'Net Income TTM', 'EPS TTM', 'Revenue Growth YoY',
];

export default function QuickFilterBar({ inputText, runQuery, options, tableCols, setTableCols }) {
  const [activeFilter, setActiveFilter] = useState(null);
  const [addedMetrics, setAddedMetrics] = useState([]);
  const addBtnRef = useRef(null);

  const handleFilterOpen = useCallback((id) => {
    setActiveFilter(prev => prev === id ? null : id);
  }, []);
  const handleFilterClose = useCallback(() => setActiveFilter(null), []);

  useEffect(() => {
    if (!activeFilter) return;
    const onKey = (e) => { if (e.key === 'Escape') setActiveFilter(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeFilter]);

  // UI state derived from React props
  const filters = useMemo(
    () => parseFiltersFromText(inputText, options),
    [inputText, options]
  );
  const selectedCap = useMemo(
    () => detectCapFromMetrics(filters.metrics),
    [filters.metrics]
  );

  useEffect(() => {
    const textMetricNames = filters.metrics
      .filter(m => m.shortName !== 'Market Cap')
      .map(m => m.shortName);
    if (textMetricNames.length > 0) {
      setAddedMetrics(prev => {
        const merged = [...new Set([...prev, ...textMetricNames])];
        return merged.filter(name => textMetricNames.includes(name) || prev.includes(name));
      });
    }
  }, [filters.metrics]);

  useEffect(() => {
    if (!tableCols || !setTableCols || filters.metrics.length === 0) return;

    const filterAggCodes = new Set();
    for (const m of filters.metrics) {
      const opt = options.find(o => o.shortName === m.shortName);
      if (opt) filterAggCodes.add(opt.aggCode);
    }
    if (filterAggCodes.size === 0) return;

    const fixedFields = new Set(['ticker', 'tickername']);
    const fixed = tableCols.filter(c => c && fixedFields.has(c.field));
    const rest = tableCols.filter(c => c && !fixedFields.has(c.field));

    const filtered = rest.filter(c => filterAggCodes.has(c.field));
    const other = rest.filter(c => !filterAggCodes.has(c.field));

    const currentOrder = tableCols.filter(Boolean).map(c => c.field);
    const desiredOrder = [...fixed, ...filtered, ...other].map(c => c.field);
    if (JSON.stringify(currentOrder) === JSON.stringify(desiredOrder)) return;

    setTableCols([...fixed, ...filtered, ...other]);
  }, [filters.metrics, tableCols, setTableCols, options]);

  // ─── DEFENSIVE DOM HELPER ─────────────────────────────────
  // Bypasses stale React state to grab exactly what the user sees in the text box.
  const getCurrentQueryText = useCallback(() => {
    const el = document.getElementById('query-builder');
    return el ? el.value : inputText;
  }, [inputText]);

  // ─── Filter Handlers ──────────────────────────────────────
  const handleSectorChange = (newSectors) => {
    const newText = updateTextWithSectors(getCurrentQueryText(), newSectors);
    runQuery(newText);
  };

  const handleCapChange = (capKey) => {
    let text = getCurrentQueryText();
    
    // 1. Force purge ALL existing Market Cap filters safely using Lexical Regex
    // We do NOT rely on `filters.metrics` here because it might be stale!
    text = removeFilterLine(text, 'metric', 'Market Cap');
    
    // 2. Append the new boundary clauses
    const clauses = capToTextClauses(capKey);
    for (const clause of clauses) {
      const parts = clause.match(/^(.+?)\s*([<>=!]+)\s*(.+)$/);
      if (parts) {
        text = updateTextWithMetric(text, parts[1].trim(), parts[2].trim(), parts[3].trim(), true);
      }
    }
    runQuery(text);
  };

  const handleMetricChange = (shortName, condition, value) => {
    const newText = updateTextWithMetric(getCurrentQueryText(), shortName, condition, value);
    runQuery(newText);
  };

  const handleMetricRemove = (shortName) => {
    const newText = removeFilterLine(getCurrentQueryText(), 'metric', shortName);
    setAddedMetrics(prev => prev.filter(m => m !== shortName));
    runQuery(newText);
  };

  // ─── "+ Add Filter" menu ───────────────────────────────────
  const handleAddMetric = (shortName) => {
    if (!addedMetrics.includes(shortName)) {
      setAddedMetrics(prev => [...prev, shortName]);
    }
    setActiveFilter(null);
    requestAnimationFrame(() => setActiveFilter(shortName));
  };

  const activeNonCapMetrics = filters.metrics.filter(m => m.shortName !== 'Market Cap');
  const visibleMetrics = [...new Set([
    ...addedMetrics,
    ...activeNonCapMetrics.map(m => m.shortName),
  ])];

  const availableMetrics = POPULAR_METRICS.filter(name =>
    !visibleMetrics.includes(name)
    && options.some(o => o.shortName === name)
  );

  return (
    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: { xs: 'nowrap', md: 'wrap' },
        overflowX: { xs: 'auto', md: 'visible' },
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
        maskImage: { xs: 'linear-gradient(to right, #000 88%, transparent)', md: 'none' },
        WebkitMaskImage: { xs: 'linear-gradient(to right, #000 88%, transparent)', md: 'none' },
        pb: 0.5,
        '& .MuiButton-root': {
          minWidth: { sm: 'auto !important', md: 0 },
          whiteSpace: 'nowrap',
        }
      }}>

        <SectorFilter
          selectedSectors={filters.sectors}
          onChange={handleSectorChange}
          open={activeFilter === 'sector'}
          onToggle={() => handleFilterOpen('sector')}
          onClose={handleFilterClose}
        />

        <MarketCapFilter
          selectedCap={selectedCap}
          onChange={handleCapChange}
          open={activeFilter === 'marketCap'}
          onToggle={() => handleFilterOpen('marketCap')}
          onClose={handleFilterClose}
        />

        {visibleMetrics.map(name => {
          const active = activeNonCapMetrics.find(m => m.shortName === name);
          return (
            <MetricFilter
              key={name}
              shortName={name}
              condition={active?.condition || ''}
              value={active?.value || ''}
              onChange={handleMetricChange}
              onRemove={handleMetricRemove}
              open={activeFilter === name}
              onToggle={() => handleFilterOpen(name)}
              onClose={handleFilterClose}
            />
          );
        })}

        {availableMetrics.length > 0 && (
          <>
            <Button
              ref={addBtnRef}
              size="small"
              onClick={() => handleFilterOpen('addMenu')}
              startIcon={<AddRoundedIcon sx={{ fontSize: '1rem !important' }} />}
              sx={{
                textTransform: 'none', fontSize: '0.8rem', borderRadius: '8px',
                color: 'text.secondary', minWidth: 0, px: 1.2, py: 0.4,
                border: '1px dashed rgba(0,0,0,0.15)',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              Add Filter
            </Button>
            <Popper open={activeFilter === 'addMenu'} anchorEl={addBtnRef.current}
              placement="bottom-start" transition disablePortal style={{ zIndex: 20 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                  <Paper sx={{ mt: 0.5, boxShadow: 4, borderRadius: '10px', maxHeight: 280, overflow: 'auto' }}>
                    <ClickAwayListener onClickAway={handleFilterClose}>
                      <MenuList dense>
                        {availableMetrics.map(name => {
                          const opt = options.find(o => o.shortName === name);
                          return (
                            <MenuItem key={name} onClick={() => handleAddMetric(name)}
                              sx={{ fontSize: '0.82rem', py: 0.75 }}
                            >
                              <Box>
                                <Box>{name}</Box>
                                {opt?.legend && opt.legend !== name && (
                                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                                    {opt.legend}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </>
        )}
      </Box>
    </Box>
  );
}


// import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import {
//   Box, Button, ClickAwayListener, Grow, MenuItem, MenuList,
//   Paper, Popper, Typography,
// } from '@mui/material';
// import AddRoundedIcon from '@mui/icons-material/AddRounded';
// import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

// import SectorFilter from './SectorFilter';
// import MarketCapFilter, { detectCapFromMetrics, capToTextClauses } from './MarketCapFilter';
// import MetricFilter from './MetricFilter';
// import {
//   parseFiltersFromText,
//   updateTextWithSectors,
//   updateTextWithMetric,
//   removeFilterLine,
// } from './filterSync';

// const POPULAR_METRICS = [
//   'PE', 'PS', 'ROE', 'Revenues TTM', 'Dividend Yield', 'Debt Equity',
//   'Net Income TTM', 'EPS TTM', 'Revenue Growth YoY',
// ];

// export default function QuickFilterBar({ inputText, runQuery, options, tableCols, setTableCols }) {
//   // Single active filter — opening one auto-closes the previous
//   const [activeFilter, setActiveFilter] = useState(null);
//   const [addedMetrics, setAddedMetrics] = useState([]);
//   const addBtnRef = useRef(null);

//   const handleFilterOpen = useCallback((id) => {
//     setActiveFilter(prev => prev === id ? null : id);
//   }, []);
//   const handleFilterClose = useCallback(() => setActiveFilter(null), []);

//   // Escape key closes any open filter
//   useEffect(() => {
//     if (!activeFilter) return;
//     const onKey = (e) => { if (e.key === 'Escape') setActiveFilter(null); };
//     document.addEventListener('keydown', onKey);
//     return () => document.removeEventListener('keydown', onKey);
//   }, [activeFilter]);

//   // Derive visual filter state from inputText
//   const filters = useMemo(
//     () => parseFiltersFromText(inputText, options),
//     [inputText, options]
//   );
//   const selectedCap = useMemo(
//     () => detectCapFromMetrics(filters.metrics),
//     [filters.metrics]
//   );

//   // Sync addedMetrics when text changes externally (e.g. user edits query directly)
//   useEffect(() => {
//     const textMetricNames = filters.metrics
//       .filter(m => m.shortName !== 'Market Cap')
//       .map(m => m.shortName);
//     if (textMetricNames.length > 0) {
//       setAddedMetrics(prev => {
//         const merged = [...new Set([...prev, ...textMetricNames])];
//         // Remove metrics no longer in text
//         return merged.filter(name => textMetricNames.includes(name) || prev.includes(name));
//       });
//     }
//   }, [filters.metrics]);

//   // Auto-order table columns: put filtered metric columns right after ticker/name
//   useEffect(() => {
//     if (!tableCols || !setTableCols || filters.metrics.length === 0) return;

//     // Get aggCodes for active filter metrics
//     const filterAggCodes = new Set();
//     for (const m of filters.metrics) {
//       const opt = options.find(o => o.shortName === m.shortName);
//       if (opt) filterAggCodes.add(opt.aggCode);
//     }
//     if (filterAggCodes.size === 0) return;

//     // Fixed columns that always stay at the start
//     const fixedFields = new Set(['ticker', 'tickername']);
//     const fixed = tableCols.filter(c => c && fixedFields.has(c.field));
//     const rest = tableCols.filter(c => c && !fixedFields.has(c.field));

//     // Separate filtered columns from the rest
//     const filtered = rest.filter(c => filterAggCodes.has(c.field));
//     const other = rest.filter(c => !filterAggCodes.has(c.field));

//     // Only reorder if the filtered columns aren't already right after fixed
//     const currentOrder = tableCols.filter(Boolean).map(c => c.field);
//     const desiredOrder = [...fixed, ...filtered, ...other].map(c => c.field);
//     if (JSON.stringify(currentOrder) === JSON.stringify(desiredOrder)) return;

//     setTableCols([...fixed, ...filtered, ...other]);
//   }, [filters.metrics, tableCols, setTableCols, options]);

//   // ─── Sector handlers ──────────────────────────────────────
//   const handleSectorChange = (newSectors) => {
//     const newText = updateTextWithSectors(inputText, newSectors);
//     runQuery(newText);
//   };

//   // ─── Market Cap handlers ───────────────────────────────────
//   const handleCapChange = (capKey) => {
//     let text = inputText;
//     const capMetrics = filters.metrics.filter(m => m.shortName === 'Market Cap');
//     for (const m of capMetrics) {
//       text = removeFilterLine(text, 'metric', m.shortName);
//     }
//     const clauses = capToTextClauses(capKey);
//     for (const clause of clauses) {
//       const parts = clause.match(/^(.+?)\s*([<>=!]+)\s*(.+)$/);
//       if (parts) {
//         text = updateTextWithMetric(text, parts[1].trim(), parts[2].trim(), parts[3].trim());
//       }
//     }
//     runQuery(text);
//   };

//   // ─── Metric handlers ──────────────────────────────────────
//   const handleMetricChange = (shortName, condition, value) => {
//     const newText = updateTextWithMetric(inputText, shortName, condition, value);
//     runQuery(newText);
//   };

//   const handleMetricRemove = (shortName) => {
//     const newText = removeFilterLine(inputText, 'metric', shortName);
//     setAddedMetrics(prev => prev.filter(m => m !== shortName));
//     runQuery(newText);
//   };

//   // ─── "+ Add Filter" menu ───────────────────────────────────
//   const handleAddMetric = (shortName) => {
//     if (!addedMetrics.includes(shortName)) {
//       setAddedMetrics(prev => [...prev, shortName]);
//     }
//     // Close menu first, then open the metric editor after it mounts
//     setActiveFilter(null);
//     requestAnimationFrame(() => setActiveFilter(shortName));
//   };

//   // Non-Market Cap metrics currently in the query
//   const activeNonCapMetrics = filters.metrics.filter(m => m.shortName !== 'Market Cap');
//   // Metrics shown as inline editors (added via menu + already in query)
//   const visibleMetrics = [...new Set([
//     ...addedMetrics,
//     ...activeNonCapMetrics.map(m => m.shortName),
//   ])];

//   // Available metrics to add (not already visible)
//   const availableMetrics = POPULAR_METRICS.filter(name =>
//     !visibleMetrics.includes(name)
//     && options.some(o => o.shortName === name)
//   );

//   return (
//     <Box sx={{ display: { xs: 'none', md: 'block' } }}>
//       {/* Filter buttons row */}
//       <Box sx={{
//         display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: { xs: 'nowrap', md: 'wrap' },
//         overflowX: { xs: 'auto', md: 'visible' },
//         scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
//         // Fade edge on mobile for scroll hint
//         maskImage: { xs: 'linear-gradient(to right, #000 88%, transparent)', md: 'none' },
//         WebkitMaskImage: { xs: 'linear-gradient(to right, #000 88%, transparent)', md: 'none' },
//         pb: 0.5,
//         '& .MuiButton-root': {
//           minWidth: { sm: 'auto !important', md: 0 },
//           whiteSpace: 'nowrap',
//         }
//       }}>
//         {/* <TuneRoundedIcon sx={{ fontSize: '1.1rem', color: 'text.disabled', mr: 0.25 }} /> */}

//         <SectorFilter
//           selectedSectors={filters.sectors}
//           onChange={handleSectorChange}
//           open={activeFilter === 'sector'}
//           onToggle={() => handleFilterOpen('sector')}
//           onClose={handleFilterClose}
//         />

//         <MarketCapFilter
//           selectedCap={selectedCap}
//           onChange={handleCapChange}
//           open={activeFilter === 'marketCap'}
//           onToggle={() => handleFilterOpen('marketCap')}
//           onClose={handleFilterClose}
//         />

//         {/* Inline metric filter editors */}
//         {visibleMetrics.map(name => {
//           const active = activeNonCapMetrics.find(m => m.shortName === name);
//           return (
//             <MetricFilter
//               key={name}
//               shortName={name}
//               condition={active?.condition || ''}
//               value={active?.value || ''}
//               onChange={handleMetricChange}
//               onRemove={handleMetricRemove}
//               open={activeFilter === name}
//               onToggle={() => handleFilterOpen(name)}
//               onClose={handleFilterClose}
//             />
//           );
//         })}

//         {/* "+ Add Filter" button */}
//         {availableMetrics.length > 0 && (
//           <>
//             <Button
//               ref={addBtnRef}
//               size="small"
//               onClick={() => handleFilterOpen('addMenu')}
//               startIcon={<AddRoundedIcon sx={{ fontSize: '1rem !important' }} />}
//               sx={{
//                 textTransform: 'none', fontSize: '0.8rem', borderRadius: '8px',
//                 color: 'text.secondary', minWidth: 0, px: 1.2, py: 0.4,
//                 border: '1px dashed rgba(0,0,0,0.15)',
//                 '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
//               }}
//             >
//               Add Filter
//             </Button>
//             <Popper open={activeFilter === 'addMenu'} anchorEl={addBtnRef.current}
//               placement="bottom-start" transition disablePortal style={{ zIndex: 20 }}
//             >
//               {({ TransitionProps }) => (
//                 <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                   <Paper sx={{ mt: 0.5, boxShadow: 4, borderRadius: '10px', maxHeight: 280, overflow: 'auto' }}>
//                     <ClickAwayListener onClickAway={handleFilterClose}>
//                       <MenuList dense>
//                         {availableMetrics.map(name => {
//                           const opt = options.find(o => o.shortName === name);
//                           return (
//                             <MenuItem key={name} onClick={() => handleAddMetric(name)}
//                               sx={{ fontSize: '0.82rem', py: 0.75 }}
//                             >
//                               <Box>
//                                 <Box>{name}</Box>
//                                 {opt?.legend && opt.legend !== name && (
//                                   <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
//                                     {opt.legend}
//                                   </Typography>
//                                 )}
//                               </Box>
//                             </MenuItem>
//                           );
//                         })}
//                       </MenuList>
//                     </ClickAwayListener>
//                   </Paper>
//                 </Grow>
//               )}
//             </Popper>
//           </>
//         )}
//       </Box>
//     </Box>
//   );
// }
